import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFichas, useGuardarFicha, usePuntosCatalogo } from '@/hooks/useCalidad';
import { useSubPartidas } from '@/hooks/useSubPartidas';
import { type ResultadoImportacion, leerExcelFichas } from '@/lib/importarFichas';
import { cn } from '@/lib/utils';
import type { FichaListado, TipoBloqueo, TipoFicha } from '@adelante/shared';
import {
  Camera,
  CaretDown,
  CircleNotch,
  FileXls,
  MagnifyingGlass,
  Plus,
} from '@phosphor-icons/react';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Catálogo de fichas de control (admin). Dos vistas:
 *   Fichas            → agrupadas por partida, con búsqueda (también por el
 *                       texto de los puntos) y filtros por tipo, bloqueo y estado.
 *   Puntos de control → todos los puntos de todas las fichas en una tabla, para
 *                       buscar si un punto ya existe (y detectar repetidos).
 * Más la importación desde Excel. La edición vive en /admin/calidad-fichas/:id.
 */
export function CalidadFichasPantalla() {
  const fichasQ = useFichas();
  const fichas = fichasQ.data ?? [];
  const subsQ = useSubPartidas();
  const guardar = useGuardarFicha();
  const inputExcel = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ResultadoImportacion | null>(null);
  const [importando, setImportando] = useState(false);
  const [params, setParams] = useSearchParams();
  const vista = params.get('vista') === 'puntos' ? 'puntos' : 'fichas';
  function cambiarVista(v: 'fichas' | 'puntos') {
    const n = new URLSearchParams(params);
    if (v === 'fichas') n.delete('vista');
    else n.set('vista', v);
    setParams(n, { replace: true });
  }

  async function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo) return;
    const subs = subsQ.data?.data ?? [];
    if (subs.length === 0) {
      toast.error('No se pudo leer el catálogo de sub-partidas');
      return;
    }
    try {
      setPreview(await leerExcelFichas(archivo, subs));
    } catch (err) {
      toast.error('No se pudo leer el Excel', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    }
  }

  async function importar() {
    if (!preview) return;
    setImportando(true);
    let ok = 0;
    const fallos: string[] = [];
    for (const { ficha } of preview.fichas) {
      // Si la ficha ya existe (mismo código) se reemplaza su estructura.
      const existente = fichas.find((f) => f.codigo === ficha.codigo);
      try {
        await guardar.mutateAsync({ id: existente?.id ?? null, body: ficha });
        ok++;
      } catch (err) {
        fallos.push(`${ficha.codigo}: ${err instanceof Error ? err.message : 'error'}`);
      }
    }
    setImportando(false);
    setPreview(null);
    if (fallos.length === 0) toast.success(`${ok} fichas importadas`);
    else
      toast.error(`${ok} importadas, ${fallos.length} con error`, {
        description: fallos.join('\n'),
      });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fichas de control</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Inspecciones (antes; bloquean el inicio si un punto obligatorio no cumple) y
            evaluaciones (después; generan no conformidades).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => inputExcel.current?.click()}
            disabled={subsQ.isLoading}
          >
            <FileXls className="h-4 w-4" weight="bold" aria-hidden="true" />
            Importar Excel
          </Button>
          <input
            ref={inputExcel}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={onArchivo}
          />
          <Button asChild>
            <Link to="/admin/calidad-fichas/nueva">
              <Plus className="h-4 w-4" weight="bold" aria-hidden="true" />
              Nueva ficha
            </Link>
          </Button>
        </div>
      </div>

      {/* Pestañas */}
      <div className="flex gap-6 border-b border-border">
        {(
          [
            ['fichas', `Fichas (${fichas.length})`],
            ['puntos', 'Puntos de control'],
          ] as const
        ).map(([v, l]) => (
          <button
            key={v}
            type="button"
            onClick={() => cambiarVista(v)}
            className={cn(
              '-mb-px border-b-2 pb-2 text-sm font-semibold',
              vista === v
                ? 'border-adelante-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {fichasQ.isLoading && (
        <div className="flex items-center gap-2 py-8 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      )}
      {fichasQ.error && <p className="py-8 text-destructive">Error: {fichasQ.error.message}</p>}
      {!fichasQ.isLoading &&
        !fichasQ.error &&
        (vista === 'fichas' ? <VistaFichas fichas={fichas} /> : <VistaPuntos />)}

      <Dialog open={preview !== null} onOpenChange={(o) => !o && !importando && setPreview(null)}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Importar fichas</DialogTitle>
            <DialogDescription>
              Las fichas con el mismo código se reemplazan (sus puntos anteriores quedan inactivos;
              los registros ya hechos no cambian).
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="max-h-[50vh] space-y-3 overflow-y-auto text-sm">
              <ul className="space-y-1">
                {preview.fichas.map(({ ficha }) => {
                  const n = ficha.secciones.reduce((a, s) => a + s.puntos.length, 0);
                  const oblig = ficha.secciones.reduce(
                    (a, s) => a + s.puntos.filter((p) => p.obligatorio).length,
                    0,
                  );
                  const existe = fichas.some((f) => f.codigo === ficha.codigo);
                  return (
                    <li key={ficha.codigo} className="flex justify-between gap-2">
                      <span>
                        <span className="font-mono text-xs">{ficha.codigo}</span> {ficha.nombre}
                        {existe && <span className="ml-1 text-xs text-amber-700">(reemplaza)</span>}
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {ficha.secciones.length} secc · {n} puntos · {oblig} oblig.
                      </span>
                    </li>
                  );
                })}
              </ul>
              {preview.errores.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  <p className="mb-1 font-semibold">Advertencias ({preview.errores.length})</p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {preview.errores.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} disabled={importando}>
              Cancelar
            </Button>
            <Button onClick={importar} disabled={importando || !preview?.fichas.length}>
              {importando ? 'Importando…' : `Importar ${preview?.fichas.length ?? 0} fichas`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Vista: fichas agrupadas por partida
// ============================================================================

const SELECT = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const normalizar = (t: string) =>
  t
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

function VistaFichas({ fichas }: { fichas: FichaListado[] }) {
  const puntosQ = usePuntosCatalogo();
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState<TipoFicha | ''>('');
  const [bloqueo, setBloqueo] = useState<TipoBloqueo | 'bloquean' | ''>('');
  const [estado, setEstado] = useState<'' | 'activas' | 'inactivas'>('activas');
  const [cerradas, setCerradas] = useState<Set<string>>(new Set());

  // Puntos que coinciden con la búsqueda, por ficha.
  const coincidencias = useMemo(() => {
    const m = new Map<string, number>();
    const q = normalizar(texto.trim());
    if (!q) return m;
    for (const p of puntosQ.data ?? []) {
      if (normalizar(`${p.texto} ${p.criterio ?? ''} ${p.seccion_nombre}`).includes(q))
        m.set(p.ficha_id.toUpperCase(), (m.get(p.ficha_id.toUpperCase()) ?? 0) + 1);
    }
    return m;
  }, [puntosQ.data, texto]);

  const filtradas = useMemo(() => {
    const q = normalizar(texto.trim());
    return fichas.filter((f) => {
      if (tipo && f.tipo !== tipo) return false;
      if (bloqueo === 'bloquean' && (f.tipo !== 'inspeccion' || f.bloqueo === 'ninguno'))
        return false;
      if (bloqueo && bloqueo !== 'bloquean' && (f.tipo !== 'inspeccion' || f.bloqueo !== bloqueo))
        return false;
      if (estado === 'activas' && !f.activo) return false;
      if (estado === 'inactivas' && f.activo) return false;
      if (!q) return true;
      const enFicha = normalizar(
        `${f.codigo} ${f.nombre} ${f.sub_partidas.map((s) => `${s.codigo} ${s.nombre}`).join(' ')}`,
      ).includes(q);
      return enFicha || coincidencias.has(f.id.toUpperCase());
    });
  }, [fichas, texto, tipo, bloqueo, estado, coincidencias]);

  // Agrupar por partida de la primera sub-partida (sin sub-partida → "Sin partida").
  const grupos = useMemo(() => {
    const m = new Map<string, { nombre: string; fichas: FichaListado[] }>();
    for (const f of filtradas) {
      const s = f.sub_partidas[0];
      const k = s ? s.partida_codigo : 'zz';
      const g = m.get(k) ?? {
        nombre: s ? `${s.partida_codigo} ${s.partida_nombre}` : 'Sin partida',
        fichas: [],
      };
      g.fichas.push(f);
      m.set(k, g);
    }
    for (const g of m.values())
      g.fichas.sort(
        (a, b) =>
          (a.sub_partidas[0]?.codigo ?? '').localeCompare(
            b.sub_partidas[0]?.codigo ?? '',
            undefined,
            {
              numeric: true,
            },
          ) || a.tipo.localeCompare(b.tipo),
      );
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [filtradas]);

  const toggle = (k: string) =>
    setCerradas((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[240px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar ficha, sub-partida o texto de un punto…"
            className="pl-8"
          />
        </div>
        <select
          className={SELECT}
          value={tipo}
          onChange={(e) => setTipo(e.target.value as TipoFicha | '')}
        >
          <option value="">Inspecciones y evaluaciones</option>
          <option value="inspeccion">Solo inspecciones</option>
          <option value="evaluacion">Solo evaluaciones</option>
        </select>
        <select
          className={SELECT}
          value={bloqueo}
          onChange={(e) => setBloqueo(e.target.value as typeof bloqueo)}
        >
          <option value="">Con y sin bloqueo</option>
          <option value="bloquean">Bloquean (al iniciar o completar)</option>
          <option value="inicio">Bloquea al iniciar</option>
          <option value="completar">Bloquea al completar</option>
          <option value="ninguno">No bloquea</option>
        </select>
        <select
          className={SELECT}
          value={estado}
          onChange={(e) => setEstado(e.target.value as typeof estado)}
        >
          <option value="activas">Activas</option>
          <option value="inactivas">Inactivas</option>
          <option value="">Todas</option>
        </select>
      </div>
      <p className="text-xs text-muted-foreground">
        {filtradas.length} de {fichas.length} fichas
      </p>

      {grupos.length === 0 && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Ninguna ficha coincide con la búsqueda.
        </Card>
      )}

      {grupos.map(([k, g]) => {
        const abierta = !cerradas.has(k);
        return (
          <Card key={k} className="overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(k)}
              className="flex w-full items-center gap-2 border-b border-border bg-zinc-50 px-4 py-2.5 text-left"
              aria-expanded={abierta}
            >
              <CaretDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  !abierta && '-rotate-90',
                )}
                weight="bold"
                aria-hidden="true"
              />
              <span className="flex-1 text-sm font-semibold">{g.nombre}</span>
              <span className="text-xs text-muted-foreground">{g.fichas.length} fichas</span>
            </button>
            {abierta && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sub-partida</TableHead>
                    <TableHead>Ficha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-center">Puntos</TableHead>
                    <TableHead className="text-center">Oblig.</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.fichas.map((f) => {
                    const n = coincidencias.get(f.id.toUpperCase());
                    return (
                      <TableRow key={f.id}>
                        <TableCell className="text-xs">
                          {f.sub_partidas.map((s) => (
                            <span key={s.sub_partida_id} className="block">
                              <span className="font-mono">{s.codigo}</span> {s.nombre}
                            </span>
                          ))}
                          {f.sub_partidas.length === 0 && '—'}
                        </TableCell>
                        <TableCell>
                          <span className="block font-medium">{f.nombre}</span>
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {f.codigo}
                          </span>
                          {n !== undefined && (
                            <span className="ml-2 rounded bg-yellow-100 px-1 text-[11px] text-yellow-900">
                              {n} punto{n === 1 ? '' : 's'} coincide{n === 1 ? '' : 'n'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={f.tipo === 'inspeccion' ? 'default' : 'secondary'}>
                            {f.tipo === 'inspeccion' ? 'Inspección' : 'Evaluación'}
                          </Badge>
                          {f.tipo === 'inspeccion' && (
                            <span className="block text-[11px] text-muted-foreground">
                              {f.bloqueo === 'inicio'
                                ? 'bloquea al iniciar'
                                : f.bloqueo === 'completar'
                                  ? 'bloquea al completar'
                                  : 'no bloquea'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{f.n_puntos}</TableCell>
                        <TableCell className="text-center tabular-nums">
                          {f.n_obligatorios}
                        </TableCell>
                        <TableCell className="text-center">
                          {f.activo ? (
                            <Badge className="bg-adelante-primary text-adelante-foreground hover:bg-adelante-primary-dark">
                              Activa
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactiva</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" asChild>
                            <Link to={`/admin/calidad-fichas/${f.id}`}>Editar</Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================================
// Vista: todos los puntos de control
// ============================================================================

function VistaPuntos() {
  const puntosQ = usePuntosCatalogo();
  const navigate = useNavigate();
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState<TipoFicha | ''>('');
  const [obligatorio, setObligatorio] = useState<'' | 'si' | 'no'>('');
  const [repetidos, setRepetidos] = useState(false);

  const puntos = (puntosQ.data ?? []).filter((p) => p.activo);
  // Cuántas veces aparece cada texto (normalizado) en distintas fichas.
  const veces = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of puntos)
      m.set(normalizar(p.texto.trim()), (m.get(normalizar(p.texto.trim())) ?? 0) + 1);
    return m;
  }, [puntos]);

  const q = normalizar(texto.trim());
  const lista = puntos.filter((p) => {
    if (tipo && p.tipo !== tipo) return false;
    if (obligatorio && p.obligatorio !== (obligatorio === 'si')) return false;
    if (repetidos && (veces.get(normalizar(p.texto.trim())) ?? 0) < 2) return false;
    if (!q) return true;
    return normalizar(
      `${p.texto} ${p.criterio ?? ''} ${p.seccion_nombre} ${p.ficha_nombre} ${p.ficha_codigo} ${p.sub_partida_revisada_codigo ?? ''} ${p.sub_partida_revisada_nombre ?? ''}`,
    ).includes(q);
  });
  if (repetidos) lista.sort((a, b) => normalizar(a.texto).localeCompare(normalizar(b.texto)));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[240px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <Input
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="¿Ya existe? Buscar un punto (ej. traslape, plomo, curador)…"
            className="pl-8"
          />
        </div>
        <select
          className={SELECT}
          value={tipo}
          onChange={(e) => setTipo(e.target.value as TipoFicha | '')}
        >
          <option value="">Inspecciones y evaluaciones</option>
          <option value="inspeccion">Solo inspecciones</option>
          <option value="evaluacion">Solo evaluaciones</option>
        </select>
        <select
          className={SELECT}
          value={obligatorio}
          onChange={(e) => setObligatorio(e.target.value as typeof obligatorio)}
        >
          <option value="">Obligatorios y no obligatorios</option>
          <option value="si">Solo obligatorios</option>
          <option value="no">Solo no obligatorios</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={repetidos}
            onChange={(e) => setRepetidos(e.target.checked)}
          />
          Solo repetidos en varias fichas
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        {lista.length} de {puntos.length} puntos
      </p>

      {puntosQ.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
        </p>
      ) : (
        <Card className="overflow-hidden">
          <div className="max-h-[65vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Punto de control</TableHead>
                  <TableHead>Ficha / sección</TableHead>
                  <TableHead>Sub-partida revisada</TableHead>
                  <TableHead className="text-center">Oblig.</TableHead>
                  <TableHead className="text-center">Foto</TableHead>
                  <TableHead>Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((p) => {
                  const n = veces.get(normalizar(p.texto.trim())) ?? 0;
                  return (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/admin/calidad-fichas/${p.ficha_id}`)}
                      title="Abrir la ficha"
                    >
                      <TableCell>
                        <span className="font-medium">{p.texto}</span>
                        {n > 1 && (
                          <span className="ml-2 rounded bg-amber-100 px-1 text-[11px] text-amber-900">
                            en {n} fichas
                          </span>
                        )}
                        {p.criterio && (
                          <span className="block text-xs text-muted-foreground">{p.criterio}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="block">{p.ficha_nombre}</span>
                        <span className="text-muted-foreground">
                          {p.tipo === 'inspeccion' ? 'Inspección' : 'Evaluación'} ·{' '}
                          {p.seccion_nombre}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.sub_partida_revisada_codigo
                          ? `${p.sub_partida_revisada_codigo} ${p.sub_partida_revisada_nombre}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-center">{p.obligatorio ? '✓' : '—'}</TableCell>
                      <TableCell className="text-center">
                        {p.requiere_foto ? (
                          <Camera className="mx-auto h-4 w-4" weight="bold" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.tipo_valor === 'valor'
                          ? `${p.unidad ?? ''} ${p.valor_min ?? ''}${p.valor_max !== null ? `–${p.valor_max}` : p.valor_min !== null ? '+' : ''}`.trim() ||
                            'Valor'
                          : p.tipo_valor === 'calificacion'
                            ? '1–5'
                            : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
